import { Vue, Options } from "vue-class-component";
import { playersModule } from "@/store/modules/players.module";
import TableLite from "vue3-table-lite/ts";
import vSelect from "vue-select";
import { FilterTypes } from "@/store/types/players";
import { VIEW_MODES } from "@/store/modules/tablets.module";
import playerPresentationsAccessService from "@/services/da/bsn-presentations.service";
import playerGroupsAccessService from "@/services/da/player-groups.service";
import { DocumentData, FirestoreError, QueryDocumentSnapshot, QuerySnapshot } from "firebase/firestore";
import ModalComponent from "@/components/modal.component.vue";
import PlayerTile from "@/components/player-tile.component.vue";
import "@vuepic/vue-datepicker/dist/main.css";
import Datepicker from "@vuepic/vue-datepicker";
import playersService from "@/services/da/players.service";
import { Unsubscribe } from "firebase/auth";
import { timeSpanFormat } from "@/services/format.service";
import Popover from "@/components/popover.component.vue";


@Options({
  components: {
    vSelect,
    ModalComponent,
    Datepicker,
    TableLite,
    PlayerTile,
    Popover
  },
  beforeRouteEnter: async (to, from, next) => {
    const querySnapshot = await playersService.getCollectionDocs();
    await playersModule.load(querySnapshot.docs)
    next()
  },
  beforeRouteLeave: () => playersModule.reset(),
})
export default class PlayersPageComponent extends Vue {

  searchString = "";
  groupId: string[] = [];
  presentationName: string[] = [];
  primePlayerId: string[] = [];

  private selectedId = ""

  onlineFilterOn = false;
  offlineFilterOn = false;
  dtFileterOn = false;

  filterByGroup = false;
  filterByPresentation = false;
  filterByPrimePlayer = false;

  downloadTimes = [
    { hours: 0, minutes: 0, seconds: 0 },
    { hours: 0, minutes: 0, seconds: 0 }
  ];
  downloadTimesDefault = true;

  playersProcessing: string[] = [];

  liveUpdateSubscription?: Unsubscribe

  get items() {
    return playersModule.filtered.map(player => {
      let { Uptime } = player
      Uptime = Uptime != undefined ? timeSpanFormat(Uptime) : Uptime
      return { ...player, Uptime }
    });
  }
  get perPage() { return playersModule.perPage; }
  get currentPage() { return playersModule.cPage; }
  get currentPlayer() { return playersModule.cItem; }
  get primePlayers() { return playersModule.primePlayersList; }
  get online() { return playersModule.online; }
  get offline() { return playersModule.offline; }

  get groupFilterEnabled() {
    return !(this.filterByPresentation || this.filterByPrimePlayer);
  }

  get primePlayerFilterEnabled() {
    return !(this.filterByGroup || this.filterByPresentation);
  }

  get presentationFIlterEnabled() {
    return !(this.filterByPrimePlayer || this.filterByGroup);
  }

  get filtersUsed() {
    return this.onlineFilterOn || this.offlineFilterOn ||
      this.filterByGroup || this.filterByPresentation || this.filterByPrimePlayer || 
      this.dtFileterOn || this.searchString.length > 0;
  }

  playerGroups: any[] = [];
  playerPresentations: any[] = [];

  table = {
    isLoading: false,
    columns: [
      {
        label: "Serial",
        field: "Serial",
        width: "10%",
      },
      {
        label: "Name",
        field: "name",
        width: "25%",
        sortable: true,
      },
      {
        label: "Group",
        field: "group",
        width: "15%",
        sortable: true
      },
      {
        label: "Presentation",
        field: "presentation",
        width: "5%",
        sortable: true
      },
      {
        label: "Uptime",
        field: "uptime",
        width: "10%",
        sortable: true
      },
      {
        label: "Prime player",
        field: "PrimePlayerName",
        width: "20%",
        sortable: true
      },
      {
        label: "Actions",
        field: "actions",
        width: "15%",
      }
    ]
  };

  get selected() { 
    return this.items.filter((item: any) => item.id == this.selectedId)[0]
  }

  get screenshots() {
    return this.items.map((screenshot) => {
      const { ScreenshotThmbUrl, ScreenshotUrl, 
        LastHeartbeat, Id, Name } = screenshot;
      return {
        thumbnail: ScreenshotThmbUrl,
        source: ScreenshotUrl,
        title: LastHeartbeat,
        id: Id,
        link: {
          params: {id: Id},
          name: 'PlayerInnerPage',
          text: Name
        }
      };
    })
  }

  get viewMode() { return playersModule.viewMode; }
  get modes() { return VIEW_MODES; }

  mounted() {

    playerPresentationsAccessService.getCollectionDocs().then(
      snapshot => this.playerPresentations = snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => doc.data()));

    playerGroupsAccessService.getCollectionDocs().then(
      snapshot => this.playerGroups = snapshot.docs.map(doc => doc.data()));
  }

  async enableDownloadTimes() {
    await playersModule.enableDownloadTimes(!this.downloadTimesDefault ? this.downloadTimes : []);
  }

  async disableDownloadTimes() {
    await playersModule.disableDownloadTimes();
  }

  async rebootPlayers() {
    if (this.selected != undefined) {
      await playersModule.rebootCommand(this.selected.Serial);
    } else {
      await playersModule.rebootCommand();
    }
  }

  async getLogs() {
    await playersModule.getLogs();
  }

  async takeSnapshot() {
    await playersModule.takeSnapshotCommand();
    this.playersProcessing = this.items.map(item => item.id)
  }

  showPlayerDetails(id: string) {
    playersModule.playerDetails(id);
  }

  onSearch() {
    const value = (this.searchString.length < 1 ? undefined : this.searchString);
    playersModule.filter({ value, type: FilterTypes.MATCH });
  }

  onFilterByGroup() {
    this.filterByGroup = this.groupId.length > 0;
    playersModule.filter({ value: this.groupId, type: FilterTypes.GROUP });
  }

  onFilterByPresenation() {
    this.filterByPresentation = this.presentationName.length > 0;
    playersModule.filter({ value: this.presentationName, type: FilterTypes.PRESENTATION });
  }

  onFilterByPrimePlayer() {
    this.filterByPrimePlayer = this.primePlayerId.length > 0
    playersModule.filter({ value: this.primePlayerId, type: FilterTypes.PRIME_PLAYER });
  }

  onStatusFilterChange() {
    playersModule.filter({ value: this.onlineFilterOn, type: FilterTypes.ONLINE });
    playersModule.filter({ value: this.offlineFilterOn, type: FilterTypes.OFFLINE });
  }

  onDtFilterChange() {
    this.filterByGroup = this.groupId.length > 0;
    playersModule.filter({ value: this.dtFileterOn, type: FilterTypes.DT_DISABLED });
  }

  onDTModalOpen(dt: any) {
    this.downloadTimes = [
      { hours: 0, minutes: 0, seconds: 0 },
      { hours: 0, minutes: 0, seconds: 0 }
    ];
    this.downloadTimesDefault = true;
  }

  switchView(mode: VIEW_MODES) {
    playersModule.viewModeUpdate(mode);

    if (mode == VIEW_MODES.GRID) {
      this.liveUpdateSubscription = playersService.getSnaphostCollection(
        (snapshot: QuerySnapshot<DocumentData>) => {
          playersModule.load(snapshot.docs)
        },
        (error: FirestoreError) => console.error(error),
        () => console.log('complete')
      )
    } else if (this.liveUpdateSubscription != undefined) {
      this.liveUpdateSubscription()
    }
  }

  resetComponentState() {
    this.selectedId = ""
  }

  onSelect(id: string) {
    this.selectedId = id
  }

  async deletePlayer() {
    if (this.selected != undefined) {
      playersService.delete(this.selected.ref)
      const querySnapshot = await playersService.getCollectionDocs();
      await playersModule.load(querySnapshot.docs)
    }
  }

  unmounted() {
    if (this.liveUpdateSubscription != undefined) {
      this.liveUpdateSubscription()
    }
  }
}
